import React, { useEffect } from "react";
import Layout from "../layout/Layout";
import { Helmet } from "react-helmet-async";
import { useLoading } from "../context/LoadingContext";

import PhilosophyHero from "../assets/Philosophy/PhilosophyHero.jfif";
import Nature from "../assets/Philosophy/Nature.jfif";
import Education from "../assets/Philosophy/Education.jfif";
import Women from "../assets/Philosophy/Women.jfif";
import Heritage from "../assets/Philosophy/Heritage.jfif";

function OurPhilosophy() {
  const { setLoading } = useLoading();

  useEffect(() => {
    setLoading(false);
    window.scrollTo(0, 0);
  }, [setLoading]);

  const baseUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://www.shahumumbai.com";

  const canonical = `${baseUrl}/ourphilosophy`;

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${baseUrl}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Our Philosophy",
        item: canonical,
      },
    ],
  };

  const webPageJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Our Philosophy — Shahu Mumbai",
    url: canonical,
    description:
      "Discover the philosophy behind Shahu Mumbai — a commitment to nature, education, women in craftsmanship, and the preservation of Indian heritage.",
  };

  const philosophyJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Shahu Mumbai Philosophy",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "In Harmony With Nature",
        description:
          "Beauty should never require cruelty. Shahu is committed to Ahimsa silk and exploring innovative, plant-based and recycled materials.",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Education Is a Right",
        description:
          "Shahu believes every child deserves access to education and is committed to supporting initiatives that help children remain in school and build their futures.",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Women Are the Craft",
        description:
          "Shahu stands for opportunity, dignity of labor, and fair compensation for women working in craftsmanship and fashion.",
      },
      {
        "@type": "ListItem",
        position: 4,
        name: "Heritage Is a Responsibility",
        description:
          "Shahu researches India's art, symbolism, stories, and craftsmanship and translates them into contemporary forms.",
      },
    ],
  };

  return (
    <Layout>
      <Helmet>
        {/* =====================================================
            SEO
        ====================================================== */}

        <title>
          Our Philosophy — Shahu Mumbai | Luxury With Responsibility
        </title>

        <meta
          name="description"
          content="Discover the philosophy behind Shahu Mumbai — luxury with responsibility, rooted in nature, education, women in craftsmanship, and India's living heritage."
        />

        <meta
          name="robots"
          content="index,follow,max-image-preview:large"
        />

        <meta
          name="keywords"
          content="Shahu Mumbai philosophy, Indian luxury fashion, Ahimsa silk, sustainable luxury, women artisans India, Indian heritage fashion, ethical fashion, Indian craftsmanship, heritage luxury"
        />

        <link rel="canonical" href={canonical} />

        <link
          rel="alternate"
          hrefLang="en-IN"
          href={canonical}
        />

        <link
          rel="alternate"
          hrefLang="x-default"
          href={canonical}
        />

        {/* =====================================================
            OPEN GRAPH
        ====================================================== */}

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Shahu Mumbai" />
        <meta property="og:locale" content="en_IN" />

        <meta
          property="og:title"
          content="Our Philosophy — Shahu Mumbai"
        />

        <meta
          property="og:description"
          content="Luxury with responsibility — our commitment to nature, education, women in craftsmanship, and India's living heritage."
        />

        <meta property="og:url" content={canonical} />

        <meta
          property="og:image"
          content={`${baseUrl}/og/philosophy.jpg`}
        />

        <meta
          property="og:image:alt"
          content="Shahu Mumbai — Our Philosophy"
        />

        {/* =====================================================
            TWITTER
        ====================================================== */}

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Our Philosophy — Shahu Mumbai"
        />

        <meta
          name="twitter:description"
          content="Luxury with responsibility — nature, education, women in craftsmanship, and India's living heritage."
        />

        <meta
          name="twitter:image"
          content={`${baseUrl}/og/philosophy.jpg`}
        />

        {/* =====================================================
            STRUCTURED DATA
        ====================================================== */}

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(webPageJsonLd)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(philosophyJsonLd)}
        </script>
      </Helmet>

      <main className="bg-[#F2E8E5] text-[#30261D] overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}

        <section className="relative w-full min-h-[680px] sm:min-h-[720px] md:h-[88vh] md:min-h-[620px] overflow-hidden">

          {/* Hero Image */}
          <img
            src={PhilosophyHero}
            alt="Shahu Mumbai philosophy"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />

          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/45" />

          {/* Bottom gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/65" />

          {/* Hero Content */}
          <div
            className="
      relative z-10
      min-h-[680px] sm:min-h-[720px] md:h-[88vh] md:min-h-[620px]
      flex flex-col
      justify-between
      px-6 sm:px-8 md:px-12 lg:px-20
      pt-10 sm:pt-12 md:pt-14
      pb-10 sm:pb-12 md:pb-14
      text-white
    "
          >

            {/* =========================
        TOP LABELS
    ========================== */}

            <div className="flex justify-between items-start gap-4">

              <p className="uppercase tracking-[0.25em] text-[8px] sm:text-[9px] md:text-xs text-white/80">
                Shahu Mumbai
              </p>

              <p className="uppercase tracking-[0.2em] text-[8px] sm:text-[9px] md:text-xs text-white/70 text-right">
                Our Philosophy
              </p>

            </div>


            {/* =========================
        MAIN HEADING
    ========================== */}

            <div className="w-full">

              <p className="uppercase tracking-[0.22em] text-[8px] sm:text-[9px] md:text-xs text-white/80 mb-5 sm:mb-6 md:mb-7">
                Luxury with responsibility
              </p>

              <h1
                className="
          font-serif
          font-light
          tracking-[-0.025em]
          leading-[0.88]
          text-[2.8rem]
          sm:text-[4rem]
          md:text-[6.5rem]
          lg:text-[8rem]
          xl:text-[8.5rem]
          max-w-full
        "
              >

                <span className="block whitespace-nowrap">
                  LUXURY
                </span>

                <span className="block whitespace-nowrap">
                  CARRIES
                </span>

                <span className="block whitespace-nowrap text-[2.65rem] sm:text-[3.8rem] md:text-[6.2rem] lg:text-[7.7rem] xl:text-[8.2rem]">
                  <span className="italic font-light">
                    A RESPONSIBILITY
                  </span>
                </span>

              </h1>

            </div>


            {/* =========================
        BOTTOM CONTENT
    ========================== */}

            <div className="flex items-end justify-between">

              <p className="max-w-[210px] sm:max-w-xs text-[10px] sm:text-xs md:text-sm leading-relaxed text-white/75">
                To the culture that inspires it.
                <br />
                To the hands that create it.
              </p>

              {/* Scroll indicator */}
              <div className="hidden md:flex flex-col items-center">

                <span className="text-[9px] uppercase tracking-[0.3em] rotate-90 mb-8 text-white/70">
                  Scroll
                </span>

                <div className="w-px h-14 bg-white/50" />

              </div>

            </div>

          </div>

        </section>
        {/* =====================================================
            OPENING STATEMENT
        ====================================================== */}

        <section className="relative py-28 md:py-40 px-6 md:px-12 lg:px-20">

          <div className="max-w-6xl mx-auto">

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

              <div className="lg:col-span-3">

                <p className="uppercase tracking-[0.3em] text-[10px] md:text-xs text-[#806E5D]">
                  Our Belief
                </p>

                <div className="mt-5 w-12 h-px bg-[#806E5D]" />

              </div>

              <div className="lg:col-span-8 lg:col-start-5">

                <p className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.1] text-[#3B2E1E]">
                  At Shahu, we believe luxury carries a responsibility
                </p>

                <div className="mt-14 space-y-3 text-lg md:text-xl text-[#6B5B4D]">

                  <p>To the culture that inspires it</p>

                  <p>To the hands that create it</p>

                  <p>To the nature that gives us our materials.</p>

                  <p>
                    And to the generations that will inherit what we leave
                    behind
                  </p>

                </div>

                <div className="mt-16 flex items-center gap-5">

                  <div className="w-16 h-px bg-[#3B2E1E]/40" />

                  <p className="uppercase tracking-[0.25em] text-[10px] text-[#806E5D]">
                    Four beliefs
                  </p>

                </div>

              </div>

            </div>

          </div>
        </section>

        {/* =====================================================
            01 — NATURE
        ====================================================== */}

        <section className="relative bg-[#3B2E1E] text-[#F6E9E3]">

          <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[850px]">

            {/* Image */}

            <div className="relative min-h-[550px] lg:min-h-[850px] overflow-hidden">

              <img
                src={Nature}
                alt="Nature and Ahimsa silk at Shahu Mumbai"
                loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2000ms] hover:scale-105"
              />

              <div className="absolute inset-0 bg-black/15" />

              <div className="absolute top-10 left-6 md:left-12">

                <span className="font-serif text-8xl md:text-[10rem] text-white/20">
                  01
                </span>

              </div>

            </div>

            {/* Text */}

            <div className="flex items-center px-6 py-20 md:px-12 lg:px-20">

              <div className="max-w-xl">

                <p className="uppercase tracking-[0.3em] text-[10px] text-[#D6BDAE] mb-6">
                  In Harmony With Nature
                </p>

                <h2 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.9]">
                  BEAUTY
                  <br />
                  SHOULD NEVER
                  <br />
                  <span className="italic font-light">
                    REQUIRE CRUELTY
                  </span>
                </h2>

                <div className="mt-12 space-y-6 text-sm md:text-base leading-[1.9] text-[#D8C9C1]">

                  <p>
                    Ever since we laid our foundations, we made a conscious
                    decision to create luxury in harmony with the nature and
                    living world around us
                  </p>

                  <p>
                    Our silk scarves are made using{" "}
                    <strong className="text-[#F6E9E3]">
                      Ahimsa silk
                    </strong>
                    , a form of silk production that allows the silkworm to
                    complete its natural life cycle before the fibres are
                    collected
                  </p>

                  <p>
                    For us, this is more than a material choice. It is an
                    expression of gratitude
                  </p>

                  <p>
                    Nature has given us color, fibre, texture, inspiration and
                    life itself. We believe the least we can do is to create
                    our creations with respect and love for what surrounds us
                  </p>

                  <p>
                    As Shahu grows, this commitment will extend beyond silk. We
                    are currently exploring innovative, plant-based and
                    recycled options for our raw material sourcing, including
                    fruit-derived alternatives to conventional animal leather
                  </p>

                </div>

                {/* Ahimsa stat */}

                <div className="mt-14 pt-8 border-t border-white/20 grid grid-cols-2 gap-8">

                  <div>
                    <p className="font-serif text-4xl md:text-5xl">
                      45–50
                    </p>

                    <p className="mt-2 uppercase tracking-[0.2em] text-[9px] text-[#BFAEA5]">
                      Days of silk production
                    </p>
                  </div>

                  <div>
                    <p className="font-serif text-4xl md:text-5xl italic">
                      Ahimsa
                    </p>

                    <p className="mt-2 uppercase tracking-[0.2em] text-[9px] text-[#BFAEA5]">
                      Silk philosophy
                    </p>
                  </div>

                </div>

                <p className="mt-12 font-serif text-xl md:text-2xl italic text-[#F6E9E3] leading-relaxed">
                  We do believe luxury and compassion can exist together
                  without compromising on either
                </p>

              </div>

            </div>

          </div>
        </section>

        {/* =====================================================
            TRANSITION
        ====================================================== */}

        <section className="py-20 md:py-28 px-6 text-center bg-[#F2E8E5]">

          <p className="font-serif text-3xl md:text-5xl lg:text-6xl italic text-[#3B2E1E] max-w-4xl mx-auto leading-tight">
            “What we create should leave the world with more possibility,
            not less”
          </p>

        </section>

        {/* =====================================================
            02 — EDUCATION
        ====================================================== */}

        <section className="relative">

          <div className="relative h-[750px] md:h-[850px] overflow-hidden">

            <img
              src={Education}
              alt="Education and future generations"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover object-[center_35%] md:object-[center_20%]"

            />

            <div className="absolute inset-0 bg-black/45" />

            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent" />

            <div className="relative z-10 h-full flex items-center px-6 md:px-12 lg:px-20">

              <div className="max-w-4xl text-white">

                <div className="flex items-center gap-6 mb-8">

                  <span className="font-serif text-7xl md:text-9xl text-white/30">
                    02
                  </span>

                  <span className="uppercase tracking-[0.3em] text-[10px] md:text-xs text-white/70">
                    Our Second Belief
                  </span>

                </div>

                <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.85]">
                  EDUCATION
                  <br />
                  <span className="italic font-light">
                    IS A RIGHT
                  </span>
                </h2>

                <p className="mt-10 text-lg md:text-2xl max-w-2xl leading-relaxed text-white/85 font-serif italic">
                  Every child deserves the opportunity to discover, learn,
                  and build
                </p>

              </div>

            </div>

          </div>

          <div className="bg-[#E7D9D4] px-6 md:px-12 lg:px-20 py-24">

            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">

              <div className="lg:col-span-4">

                <p className="uppercase tracking-[0.3em] text-[10px] text-[#806E5D]">
                  A belief in tomorrow
                </p>

                <h3 className="mt-5 font-serif text-3xl md:text-4xl leading-tight">
                  A child's future should never be determined by the
                  circumstances into which they are born
                </h3>

              </div>

              <div className="lg:col-span-7 lg:col-start-6 text-[#66594F] text-sm md:text-base leading-[1.95]">

                <p>
                  As a home grown fashion house founded by a learning
                  enthusiast, we firmly stand with the ideology that a child's
                  future should never be determined by the circumstances into
                  which they are born
                </p>

                <p className="mt-7">
                  Across India, there are children for whom education still
                  remains a choice between survival and opportunity. Too often,
                  survival wins. We, along with your support want to help change
                  that
                </p>

                <p className="mt-7">
                  With every step of progress that we make in this beautiful
                  journey of exploring heritage fashion, we are committed to
                  supporting initiatives that help children access education,
                  remain in school, and build futures that extend beyond the
                  circumstances of their childhood
                </p>

                <p className="mt-7">
                  Because education should never be a privilege reserved for
                  those who can afford it
                </p>

                <div className="mt-12 grid grid-cols-3 border-y border-[#3B2E1E]/20">

                  <div className="py-7 text-center border-r border-[#3B2E1E]/20">
                    <p className="font-serif text-2xl md:text-3xl">
                      Discover
                    </p>
                  </div>

                  <div className="py-7 text-center border-r border-[#3B2E1E]/20">
                    <p className="font-serif text-2xl md:text-3xl">
                      Learn
                    </p>
                  </div>

                  <div className="py-7 text-center">
                    <p className="font-serif text-2xl md:text-3xl">
                      Build
                    </p>
                  </div>

                </div>

                <p className="mt-12 font-serif text-2xl md:text-3xl italic text-[#3B2E1E]">
                  Children are the future.
                </p>

              </div>

            </div>

          </div>

        </section>

        {/* =====================================================
            03 — WOMEN
        ====================================================== */}

        <section className="bg-[#F2E8E5]">

          <div className="relative h-[780px] md:h-[900px] overflow-hidden">

            <img
              src={Women}
              alt="Women and craftsmanship at Shahu Mumbai"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover object-[center_35%] md:object-[center_20%]"
            />

            <div className="absolute inset-0 bg-black/35" />

            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

            <div className="relative z-10 h-full flex flex-col justify-between px-6 md:px-12 lg:px-20 py-12 md:py-16 text-white">

              <div className="flex justify-between items-start">

                <span className="font-serif text-8xl md:text-[10rem] text-white/25">
                  03
                </span>

                <p className="uppercase tracking-[0.3em] text-[10px] md:text-xs text-white/75">
                  Women Are the Craft
                </p>

              </div>

              <div>

                <p className="uppercase tracking-[0.3em] text-[10px] md:text-xs text-white/70 mb-5">
                  The hands behind Shahu
                </p>

                <h2 className="font-serif text-6xl md:text-8xl lg:text-[9rem] leading-[0.8]">
                  WOMEN
                  <br />
                  ARE THE
                  <br />
                  <span className="italic font-light">
                    CRAFT
                  </span>
                </h2>

              </div>

            </div>

          </div>

          <div className="px-6 md:px-12 lg:px-20 py-24">

            <div className="max-w-6xl mx-auto">

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

                <div className="lg:col-span-4">

                  <p className="font-serif text-3xl md:text-4xl leading-tight">
                    Shahu was born from women
                  </p>

                  <p className="mt-5 text-lg italic text-[#806E5D]">
                    Women who created, stitched, and taught
                  </p>

                </div>

                <div className="lg:col-span-7 lg:col-start-6 text-sm md:text-base text-[#66594F] leading-[1.95]">

                  <p>
                    Women who carried craftsmanship from one generation to the
                    next
                  </p>

                  <p className="mt-7">
                    For decades, the women of the Shahu family have been part
                    of the world of fashion and garment craftsmanship. Their
                    hands, knowledge and discipline form part of the foundation
                    upon which this house stands
                  </p>

                  <p className="mt-7">
                    The world has witnessed what women are capable of creating,
                    especially in the fashion industry. Women continue to make
                    some of its most important contributions from design and
                    embroidery to tailoring, finishing and craftsmanship
                  </p>

                  <p className="mt-7">
                    We stand with the hands behind beautiful work that deserve
                    more than recognition
                  </p>

                </div>

              </div>

              {/* Values */}

              <div className="mt-24 border-t border-b border-[#3B2E1E]/20">

                <div className="grid grid-cols-1 md:grid-cols-3">

                  <div className="py-12 md:py-16 md:border-r border-[#3B2E1E]/20">

                    <p className="uppercase tracking-[0.25em] text-[10px] text-[#806E5D]">
                      We believe in
                    </p>

                    <h3 className="mt-5 font-serif text-4xl md:text-5xl">
                      Opportunity
                    </h3>

                  </div>

                  <div className="py-12 md:py-16 md:px-10 md:border-r border-[#3B2E1E]/20">

                    <p className="uppercase tracking-[0.25em] text-[10px] text-[#806E5D]">
                      We believe in
                    </p>

                    <h3 className="mt-5 font-serif text-4xl md:text-5xl">
                      Dignity
                    </h3>

                    <p className="mt-3 text-sm text-[#806E5D]">
                      Of labor
                    </p>

                  </div>

                  <div className="py-12 md:py-16 md:pl-10">

                    <p className="uppercase tracking-[0.25em] text-[10px] text-[#806E5D]">
                      We believe in
                    </p>

                    <h3 className="mt-5 font-serif text-4xl md:text-5xl">
                      Fairness
                    </h3>

                    <p className="mt-3 text-sm text-[#806E5D]">
                      Fair compensation.
                    </p>

                  </div>

                </div>

              </div>

              <p className="max-w-3xl mt-16 font-serif text-2xl md:text-4xl leading-relaxed text-[#3B2E1E]">
                At Shahu, we are dedicated to building a place of trust and
                opportunity for women, where they can create and celebrate
                their craft
              </p>

            </div>

          </div>

        </section>

        {/* =====================================================
            04 — HERITAGE
        ====================================================== */}

        <section className="bg-[#262019] text-[#F5E8E1]">

          {/* Heritage Hero */}

          <div className="relative h-[680px] sm:h-[740px] md:h-[850px] overflow-hidden">

            <img
              src={Heritage}
              alt="Indian heritage and craftsmanship"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover object-[center_35%] md:object-[center_20%]"

            />

            {/* Dark overlay */}
            <div className="absolute inset-0 bg-black/45" />

            {/* Bottom gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-transparent to-black/75" />

            {/* Content */}

            <div
              className="
        relative z-10
        h-full
        flex flex-col
        justify-between
        px-6 sm:px-8 md:px-12 lg:px-20
        py-10 sm:py-12 md:py-16
      "
            >

              {/* =========================
          TOP
      ========================== */}

              <div className="flex justify-between items-start gap-5">

                <span
                  className="
            font-serif
            text-7xl
            sm:text-8xl
            md:text-[10rem]
            leading-none
            text-white/20
          "
                >
                  04
                </span>

                <p
                  className="
            uppercase
            tracking-[0.22em]
            text-[8px]
            sm:text-[9px]
            md:text-xs
            text-white/70
            text-right
            max-w-[130px]
            sm:max-w-none
          "
                >
                  Heritage Is a Responsibility
                </p>

              </div>


              {/* =========================
          MAIN HEADING
      ========================== */}

              <div className="w-full">

                <p
                  className="
            uppercase
            tracking-[0.25em]
            text-[8px]
            sm:text-[9px]
            md:text-xs
            text-white/70
            mb-6
          "
                >
                  Remember · Reinterpret · Create
                </p>


                <h2
                  className="
            font-serif
            font-light
            tracking-[-0.025em]
            leading-[0.88]
            text-white
            w-full
          "
                >

                  {/* HERITAGE */}

                  <span className="block whitespace-nowrap text-[3.1rem] sm:text-[4.5rem] md:text-[7rem] lg:text-[8rem]">
                    HERITAGE
                  </span>


                  {/* IS A */}

                  <span className="block whitespace-nowrap text-[3.1rem] sm:text-[4.5rem] md:text-[7rem] lg:text-[8rem]">
                    IS A
                  </span>


                  {/* RESPONSIBILITY */}

                  <span
                    className="
              block
              whitespace-nowrap
              text-[2.55rem]
              sm:text-[3.8rem]
              md:text-[6.2rem]
              lg:text-[7.4rem]
            "
                  >
                    <span className="italic font-light">
                      RESPONSIBILITY
                    </span>
                  </span>

                </h2>

              </div>


              {/* =========================
          BOTTOM
      ========================== */}

              <div>

                <div className="w-10 h-px bg-white/50 mb-5" />

                <p className="max-w-[260px] text-[10px] sm:text-xs md:text-sm text-white/70 leading-relaxed">
                  Heritage is not something to be admired from a distance.
                  It is something to be carried forward.
                </p>

              </div>

            </div>

          </div>


          {/* =====================================================
      HERITAGE COPY
  ====================================================== */}

          <div className="px-6 md:px-12 lg:px-20 py-24 md:py-28">

            <div className="max-w-6xl mx-auto">

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

                <div className="lg:col-span-5">

                  <p className="font-serif text-3xl md:text-5xl leading-tight">
                    Heritage is not something to be admired from a distance.
                  </p>

                  <p className="mt-6 font-serif text-3xl md:text-5xl italic text-[#CDB9AC] leading-tight">
                    It is something to be carried forward.
                  </p>

                </div>


                <div className="lg:col-span-6 lg:col-start-7 text-sm md:text-base text-[#CBBDB5] leading-[1.95]">

                  <p>
                    India has given the world thousands of years of art,
                    philosophy, architecture, textiles, stories of creation,
                    and craftsmanship.
                  </p>

                  <p className="mt-7">
                    Some stories are celebrated. Others remain hidden in
                    manuscripts, temples, villages, family archives and
                    traditions passed quietly from one generation to another.
                  </p>

                  <p className="mt-7">
                    We believe these stories deserve another life.
                  </p>

                </div>

              </div>


              {/* =========================
          DISCOVERY
      ========================== */}

              <div className="mt-24 md:mt-28 border-y border-white/15 py-14 md:py-16">

                <div className="grid grid-cols-1 md:grid-cols-3 gap-10">

                  <div>

                    <p className="text-5xl md:text-7xl font-serif text-white/20">
                      01
                    </p>

                    <p className="mt-5 uppercase tracking-[0.25em] text-[10px] text-[#CDB9AC]">
                      Discover
                    </p>

                    <p className="mt-4 text-sm text-[#CBBDB5] leading-relaxed">
                      We search through art, stories, symbolism and craftsmanship.
                    </p>

                  </div>


                  <div>

                    <p className="text-5xl md:text-7xl font-serif text-white/20">
                      02
                    </p>

                    <p className="mt-5 uppercase tracking-[0.25em] text-[10px] text-[#CDB9AC]">
                      Research
                    </p>

                    <p className="mt-4 text-sm text-[#CBBDB5] leading-relaxed">
                      We understand the meaning and history behind what inspires us.
                    </p>

                  </div>


                  <div>

                    <p className="text-5xl md:text-7xl font-serif text-white/20">
                      03
                    </p>

                    <p className="mt-5 uppercase tracking-[0.25em] text-[10px] text-[#CDB9AC]">
                      Reinterpret
                    </p>

                    <p className="mt-4 text-sm text-[#CBBDB5] leading-relaxed">
                      We allow heritage to live through contemporary forms.
                    </p>

                  </div>

                </div>

              </div>


              {/* =========================
          CLOSING COPY
      ========================== */}

              <div className="mt-20 max-w-4xl">

                <p className="text-base md:text-lg text-[#CBBDB5] leading-[1.9]">
                  We research the art, symbolism, stories, and craftsmanship
                  behind our designs before translating them into contemporary
                  forms.
                </p>

                <p className="mt-7 text-base md:text-lg text-[#CBBDB5] leading-[1.9]">
                  Our intention is not to recreate the past, but to allow it to
                  live in the present.
                </p>

                <p className="mt-12 font-serif text-2xl md:text-4xl italic text-[#F5E8E1] leading-relaxed">
                  We believe in remembering, reinterpreting, and creating with
                  an intention.
                </p>

              </div>

            </div>

          </div>

        </section>

        {/* =====================================================
            FINAL MANIFESTO
        ====================================================== */}

        <section className="relative min-h-[650px] flex items-center justify-center bg-[#E9DCD7] px-6 py-28 text-center overflow-hidden">

          {/* Decorative circles */}

          <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full border border-[#3B2E1E]/10" />

          <div className="absolute -bottom-48 -right-48 w-[600px] h-[600px] rounded-full border border-[#3B2E1E]/10" />

          <div className="relative z-10 max-w-5xl">

            <p className="uppercase tracking-[0.35em] text-[10px] md:text-xs text-[#806E5D] mb-10">
              The Shahu Philosophy
            </p>

            <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.9] text-[#3B2E1E]">

              <span className="block">
                REMEMBER
              </span>

              <span className="block italic font-light">
                REINTERPRET
              </span>

              <span className="block">
                CREATE
              </span>

            </h2>

            <div className="w-16 h-px bg-[#3B2E1E]/40 mx-auto my-12" />

            <p className="max-w-2xl mx-auto text-base md:text-lg text-[#66594F] leading-relaxed">
              To create beautifully
              <br />
              To create consciously
              <br />
              To create with meaning
            </p>

            <p className="mt-12 uppercase tracking-[0.35em] text-xs text-[#3B2E1E]">
              Shahu Mumbai
            </p>

            <p className="mt-4 font-serif text-xl md:text-2xl italic text-[#806E5D]">
              Our world, curated to be yours.
            </p>

          </div>

        </section>

      </main>
    </Layout>
  );
}

export default OurPhilosophy;